import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

/**
 * Tooltip — hover an element to show a description popup.
 * Uses a portal so the popup is never clipped by overflow:hidden ancestors.
 */
export default function Tooltip({ text, children }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos]         = useState({ x: 0, y: 0 });
  const ref = useRef(null);
  const timer = useRef(null);

  const show = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) setPos({ x: rect.left, y: rect.top });
      setVisible(true);
    }, 350);
  }, []);

  const hide = useCallback(() => {
    clearTimeout(timer.current);
    setVisible(false);
  }, []);

  if (!text) return children;

  return (
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={hide}
      style={{ position: 'relative', display: 'inline-flex' }}
    >
      {children}
      {visible && createPortal(
        <span style={{
          ...styles.tooltip,
          left: Math.max(8, pos.x - 110),
          top: pos.y - 8,
          transform: 'translateY(-100%)',
        }}>
          {text}
        </span>,
        document.body,
      )}
    </span>
  );
}

const styles = {
  tooltip: {
    position: 'fixed',
    background: '#142038',
    border: '1px solid #4a3d28',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 11,
    lineHeight: 1.4,
    color: '#d4d4e8',
    whiteSpace: 'normal',
    width: 220,
    maxWidth: 260,
    zIndex: 9999,
    pointerEvents: 'none',
    boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
  },
};
